import { MatTreeNestedDataSource } from '@angular/material/tree';
import { Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';

import { ActivatedRoute } from '@angular/router';
import { TreeService } from '@modules/policy/services/location/tree.service';
import { PolicyDataService } from '@modules/policy/services/shared/policy-data.service';
import { PolicyService } from '@modules/policy/policy.service';
import { CoverageTableComponent } from '@modules/policy/shared/components/coverage-table/coverage-table.component';
import { FormComponent } from '@modules/policy/shared/components/form/form.component';
import { FormSaveHelper } from '@modules/policy/services/shared/save';
import { SaveService } from '@modules/policy/services/shared/save.service';
import { JsonFormFetcherService } from '@modules/policy/services/shared/json-form-fetcher.service';
import { handleValidations } from '@modules/policy/shared/rules/handle-validations.rules';
import LocationTabs from '@shared/models/location.model';
import { PolicyState } from '@shared/models/state.model';
import { combineLatest, of, Subject, Subscription } from 'rxjs';
import { PremiumSyncService } from '@modules/policy/services/shared/premium-sync.service';
import { FormTabService } from 'form-tab';

@Component({
  selector: 'app-vehicle-details',
  templateUrl: './vehicle-details.component.html',
  styleUrls: ['./vehicle-details.component.scss']
})
export class VehicleDetailsComponent implements OnInit {

  policyID: string;
  tabID: string;
  nodeID: string;
  dataSource: MatTreeNestedDataSource<LocationTabs>;
  vehicleTab: string = "vehicle";
  vehicleSubtab: string = "vehicleDetails";
  coverageTab: string = "location";
  coverageSubtab: string = "coverageDetails";
  formJson: object;
  states: PolicyState;
  listName: string;
  riskStates: any;
  detailsForm: FormGroup = new FormGroup({});
  form: FormGroup = new FormGroup({});
  coverageForm: FormGroup = new FormGroup({});
  initSubscription: Subscription;
  detailsFormSubscription: Subscription;
  lob: string;
  @ViewChild("tableComponent", { static: false }) tableComponent: CoverageTableComponent;
  @ViewChild("formComponent", { static: false }) formComponent: FormComponent;
  detailsFormSubject: Subject<Object> = new Subject<Object>();
  constructor(
    private route: ActivatedRoute,
    private PolicyDataService: PolicyDataService,
    private TreeService: TreeService,
    private PolicyService: PolicyService,
    private FormSaveHelper: FormSaveHelper,
    private SaveService: SaveService,
    private JsonFormFetcherService: JsonFormFetcherService,
    private premiumSyncService: PremiumSyncService,
    private formTabService: FormTabService
  ) { }

  ngOnInit() {
    this.matTreeSubscription();
    
    this.policyID = this.PolicyDataService.getPolicyID();
    const tabsObs = this.JsonFormFetcherService.getTabs(this.policyID);
    
    this.route.params.subscribe(params => {
      this.tabID = params["vehicleId"];
      this.nodeID = params["formId"];
      
      // Reset parent form for new vehicle
      this.form = new FormGroup({});
      this.detailsForm = new FormGroup({});
      this.coverageForm = new FormGroup({});

      this.initSubscription = combineLatest([this.PolicyDataService.stateSubject, tabsObs]).subscribe(res => {
        const [states, tabs] = res;
        this.states = states;
      });
    });
  }

  buildDetailsPayload(onResult?: Function): Object {
    let formData = {};
    Object.values(this.detailsForm.getRawValue()).forEach(value => {
      if (typeof value === 'object' && value !== null) {
        formData = { ...formData, ...value };
      }
    });
    if (onResult) onResult({ formData });
    else return formData;
  }

  canDeactivate() {
    return of(true).toPromise();
  }

  saveBeforeDeactivate() {
    if (this.PolicyService.getForm(Number(this.nodeID)) != null) {
      return this.tableComponent.saveBeforeDeactivate(this.detailsForm).then(() => {
        const cascadingIds: object = {
          nodeState: [Number(this.nodeID), this.form.valid]
        };
        const cascadingStates = this.FormSaveHelper.buildStatePayload(cascadingIds);
        this.SaveService.updateStates(this.policyID, cascadingStates).subscribe();
      });
    }
    return of(null).toPromise();
  }

  ngOnDestroy() {
    if (this.initSubscription) {
      this.initSubscription.unsubscribe();
    }
    if (this.detailsFormSubscription) {
      this.detailsFormSubscription.unsubscribe();
    }
  }

  showSaveSnackbar() {
    this.formComponent.showSaveSnackbar();
    this.tableComponent.updateNavigationTree();
  }

  initForm(form: FormGroup) {
    this.detailsForm = form;
    this.form.addControl('details', this.detailsForm);
    
    if (this.formComponent) {
      this.formJson = this.formComponent.getFormJson();
      handleValidations(this.formComponent.formjson, this.detailsForm);
    }
    
    // Unsubscribe from previous subscription before creating new one
    if (this.detailsFormSubscription) {
      this.detailsFormSubscription.unsubscribe();
    }
    
    this.detailsFormSubscription = this.detailsForm.valueChanges.subscribe(forms => {
      const formData = this.buildDetailsPayload();
      if (!this.hasFormControls(this.coverageForm)) return;
      this.detailsFormSubject.next(formData);
      this.premiumSyncService.syncPremium({ ...this.tableComponent.buildCoveragesPayload(), formData });
    });

    this.setupValidationsIfFormsReady();
  }

  initCoverageForm(form: FormGroup) {
    this.coverageForm = form;
    this.form.addControl('coverages',this.coverageForm);
    this.setupValidationsIfFormsReady();

  }

  private setupValidationsIfFormsReady(): void {
    if (this.hasFormControls(this.detailsForm) && this.hasFormControls(this.coverageForm)) {
      this.formTabService.setupParameterizedValidations(this.formJson, this.detailsForm, this.coverageForm);
      
      // Register parent form AFTER both children added
      this.PolicyService.setForm(Number(this.nodeID), this.form);
      
      this.coverageForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });
      this.detailsForm.updateValueAndValidity({ onlySelf: false, emitEvent: true });
    }
  }

  private hasFormControls(form: FormGroup): boolean {
    return form && Object.keys(form.controls).length > 0;
  }

  updateNavigationTree() {
    this.PolicyDataService.getPolicyStates(this.policyID).subscribe(tree => {
      this.states = tree;
      this.PolicyDataService.updateStates(tree);
    })
  }

  matTreeSubscription() {
    this.dataSource = new MatTreeNestedDataSource<LocationTabs>();
    this.TreeService.dataSourceSubject.subscribe(data => {
      this.dataSource.data = null;
      this.dataSource.data = data;
    });
  }

}
